import type { TokenValues } from "@silversea-ssc/dotcom-read-model-types/Tokens";
import {
  InclusionContent,
  StepsGroup
} from "@silversea-ssc/ui-components-react";
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";
import { TabContent } from "./InclusionsModal";
const InclusionsModal = React.lazy(() => import("./InclusionsModal"));

type InclusionsModalContextValue = {
  inclusionsContent: InclusionContent[];
  onInclusionClick: (args: InfographicInfo) => void;
};

const InclusionsModalContext = React.createContext<InclusionsModalContextValue>(
  {
    inclusionsContent: [] as InclusionContent[],
    onInclusionClick: () => {
      ///
    }
  }
);

type InfographicInfo = {
  title: string;
  inclusionsContentTokens: TokenValues | undefined;
  selectedStepGroupId: string;
  steps: StepsGroup[];
};

type InternalInfographcInfo = Omit<InfographicInfo, "activeInclusionCodes">;

type Props = {
  inclusionsContent: InclusionContent[];
};

export const InclusionsModalProvider: React.FC<PropsWithChildren<Props>> = ({
  children,
  inclusionsContent
}) => {
  const [infographicInfo, setInfographicInfo] = useState<
    InternalInfographcInfo | undefined
  >();

  const onInclusionClick = useCallback((args: InfographicInfo) => {
    setInfographicInfo(args);
  }, []);

  const contextValue = useMemo(() => {
    return {
      onInclusionClick,
      inclusionsContent
    };
  }, [onInclusionClick, inclusionsContent]);

  return (
    <InclusionsModalContext.Provider value={contextValue}>
      {children}
      {infographicInfo && (
        <React.Suspense>
          <InclusionsModal
            title={infographicInfo.title}
            dataContentName="inclusions_modal"
            modal={{
              isOpen: true,
              onRequestClose: () => setInfographicInfo(undefined)
            }}
            size="big"
            inclusionsContentTokens={infographicInfo.inclusionsContentTokens}
            selectedStepGroupId={infographicInfo.selectedStepGroupId}
            tabs={buildTabsContent(infographicInfo.steps)}
          />
        </React.Suspense>
      )}
    </InclusionsModalContext.Provider>
  );
};

export const useInclusionsModal = () => {
  return useContext(InclusionsModalContext);
};

const buildTabsContent = (steps: StepsGroup[]) => {
  return steps.map(contentForTab).filter(withContent);
};

const contentForTab = (group: StepsGroup): TabContent => {
  const activeInclusionCodes = group.steps
    .filter((s) => s.type == "active")
    .flatMap((s) => s.inclusions);

  return { id: group.id, title: group.title, contents: activeInclusionCodes };
};

const withContent = (t: TabContent) => t.contents.length > 0;
